import { ColorField } from "@/components/cms/theme/ColorField";
import { openMediaPicker } from "@/components/cms-puck/fields/media-picker-store";
import { openImageStudio } from "@/components/cms-puck/fields/image-studio-store";
import { canRetouchAttachment } from "@/components/images/can-retouch-attachment";
import { STYLE_BG_TARGET } from "@/components/cms-puck/fields/MediaPickerField";
import { useGetAttachmentById, type Attachment } from "@/api/attachment";
import {
  attachmentThumb,
  catalogMediaKindBadge,
} from "@/lib/cms-puck/media-utils";
import { type CmsColor } from "@/lib/cms/cms-theme.constants";
import type {
  BackgroundFit,
  BackgroundImage,
  BackgroundPosition,
  GradientDirection,
} from "@/lib/cms-puck/block-style";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  PuckSafeSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/cms-puck/fields/PuckSafeSelect";
import { CMS_PALETTE_TEXT_INPUT_CLASS } from "@/components/cms/theme/cms-color-input-styles";
import { cn } from "@/lib/utils";
import { useCmsNew, type CmsNewKey } from "@/lib/cms/cms-new-i18n";

/**
 * Éditeur de MÉDIA DE FOND d'un bloc/section (champ `style.design.backgroundImage`).
 * - L'`attachmentId` est choisi via le picker média existant (host) en ciblant la
 *   sentinelle `STYLE_BG_TARGET` → écrit dans le style (pas une prop plate).
 * - L'ajustement / position / overlay sont écrits par l'`onChange` synchrone du
 *   panneau (comme le reste du Design). Aperçu via react-query `useGetAttachmentById`.
 */

/** Clés + fallbacks — ne pas résoudre cmsNew() au niveau module. */
const FIT_OPTIONS: Array<{
  value: BackgroundFit;
  labelKey: CmsNewKey;
  fallback: string;
}> = [
  { value: "cover", labelKey: "cms.new.auto.label.couvrir", fallback: "Couvrir" },
  { value: "contain", labelKey: "cms.new.auto.label.contenir", fallback: "Contenir" },
  { value: "stretch", labelKey: "cms.new.auto.label.etirer", fallback: "Étirer" },
  { value: "repeat", labelKey: "cms.new.auto.label.repeter", fallback: "Répéter" },
];

const POSITION_OPTIONS: Array<{
  value: BackgroundPosition;
  labelKey: CmsNewKey;
  fallback: string;
}> = [
  { value: "center", labelKey: "cms.new.auto.label.centre", fallback: "Centre" },
  { value: "top", labelKey: "cms.new.auto.label.haut", fallback: "Haut" },
  { value: "bottom", labelKey: "cms.new.auto.label.bas", fallback: "Bas" },
  { value: "left", labelKey: "cms.new.auto.label.gauche", fallback: "Gauche" },
  { value: "right", labelKey: "cms.new.auto.label.droite", fallback: "Droite" },
  {
    value: "top-left",
    labelKey: "cms.new.auto.label.haut_gauche",
    fallback: "Haut gauche",
  },
  {
    value: "top-right",
    labelKey: "cms.new.auto.label.haut_droite",
    fallback: "Haut droite",
  },
  {
    value: "bottom-left",
    labelKey: "cms.new.auto.label.bas_gauche",
    fallback: "Bas gauche",
  },
  {
    value: "bottom-right",
    labelKey: "cms.new.auto.label.bas_droite",
    fallback: "Bas droite",
  },
];

const DIRECTION_OPTIONS: Array<{
  value: GradientDirection;
  labelKey: CmsNewKey;
  fallback: string;
}> = [
  {
    value: "to-bottom",
    labelKey: "cms.new.auto.label.haut_bas",
    fallback: "Haut → Bas",
  },
  {
    value: "to-top",
    labelKey: "cms.new.auto.label.bas_haut",
    fallback: "Bas → Haut",
  },
  {
    value: "to-right",
    labelKey: "cms.new.auto.label.gauche_droite",
    fallback: "Gauche → Droite",
  },
  {
    value: "to-left",
    labelKey: "cms.new.auto.label.droite_gauche",
    fallback: "Droite → Gauche",
  },
  {
    value: "to-bottom-right",
    labelKey: "cms.new.auto.label.diagonale",
    fallback: "Diagonale ↘",
  },
  {
    value: "to-bottom-left",
    labelKey: "cms.new.auto.label.diagonale",
    fallback: "Diagonale ↙",
  },
];

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Label className="text-xs font-medium text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

export function BackgroundImageField({
  value,
  onChange,
  colors,
}: {
  value: BackgroundImage;
  onChange: (next: BackgroundImage) => void;
  colors: CmsColor[];
}) {
  const cmsNew = useCmsNew();
  const { data } = useGetAttachmentById(value.attachmentId ?? "");
  const att = value.attachmentId ? (data as Attachment | undefined) : undefined;
  const thumb = att ? attachmentThumb(att) : undefined;
  const kindBadge = att ? catalogMediaKindBadge(att.kind) : "";

  const setBg = (patch: Partial<BackgroundImage>) =>
    onChange({ ...value, ...patch });
  const setOverlay = (patch: Partial<BackgroundImage["overlay"]>) =>
    onChange({ ...value, overlay: { ...value.overlay, ...patch } });

  return (
    <div className="space-y-4">
      {/* Sélection du média */}
      <div className="flex items-center gap-3">
        {thumb ? (
          <div className="relative">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={thumb}
              alt={cmsNew("cms.new.auto.alt.d41d8c", "")}
              className="h-14 w-14 rounded border border-slate-200 object-cover"
            />
            {kindBadge ? (
              <span className="absolute bottom-0.5 left-0.5 rounded bg-black/60 px-1 py-0.5 text-[9px] font-medium text-white">
                {kindBadge}
              </span>
            ) : null}
          </div>
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded border border-dashed border-slate-200 bg-slate-50 text-[10px] text-slate-400">
            {cmsNew("cms.new.fields.backgroundImage.thumbNone", "Aucun")}
          </div>
        )}
        <div className="flex flex-col gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              openMediaPicker(STYLE_BG_TARGET, {
                currentAttachmentId: value.attachmentId || undefined,
                initialTab:
                  att?.kind === "embed" || att?.kind === "video"
                    ? "upload"
                    : "library",
              })
            }
          >
            {value.attachmentId
              ? cmsNew("cms.new.fields.backgroundImage.changeMedia", "Changer le média")
              : cmsNew("cms.new.fields.backgroundImage.chooseMedia", "Choisir un média")}
          </Button>
          {att && canRetouchAttachment(att) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => openImageStudio(att)}
            >
              {cmsNew("cms.new.pickers.media.retouch", "Retoucher")}
            </Button>
          )}
          {value.attachmentId && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setBg({ attachmentId: null })}
            >
              {cmsNew("cms.new.auto.title.retirer", "Retirer")}
            </Button>
          )}
        </div>
      </div>

      {/* Ajustement + position (utiles avec un média sélectionné) */}
      {value.attachmentId && (
        <>
          <Row label={cmsNew("cms.new.auto.label.ajustement", "Ajustement")}>
            <Select
              value={value.fit}
              onValueChange={(v) => setBg({ fit: v as BackgroundFit })}
            >
              <SelectTrigger className="h-9 w-40 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {cmsNew(o.labelKey, o.fallback)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Row>
          <Row label={cmsNew("cms.new.auto.label.position", "Position")}>
            <Select
              value={value.position}
              onValueChange={(v) =>
                setBg({ position: v as BackgroundPosition })
              }
            >
              <SelectTrigger className="h-9 w-40 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {POSITION_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {cmsNew(o.labelKey, o.fallback)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Row>
        </>
      )}

      {/* Dégradé overlay (par-dessus l'image ; from=to ⇒ teinte unie) */}
      <div className="space-y-3 rounded-lg border border-slate-200/70 bg-slate-50/60 p-3">
        <label className="flex items-center justify-between gap-3 text-xs font-medium text-slate-600">
          {cmsNew("cms.new.fields.backgroundImage.gradientOverlay", "Dégradé par-dessus")}
          <input
            type="checkbox"
            checked={value.overlay.enabled}
            onChange={(e) => setOverlay({ enabled: e.target.checked })}
            className="h-4 w-4 accent-slate-900"
          />
        </label>

        {value.overlay.enabled && (
          <div className="space-y-3">
            <ColorField
              label={cmsNew("cms.new.auto.label.couleur_de_debut", "Couleur de début")}
              colors={colors}
              value={value.overlay.from}
              onChange={(from) => setOverlay({ from })}
            />
            <ColorField
              label={cmsNew("cms.new.auto.label.couleur_de_fin_vide_teinte_unie", "Couleur de fin (vide = teinte unie)")}
              colors={colors}
              value={value.overlay.to}
              onChange={(to) => setOverlay({ to })}
            />
            <Row label={cmsNew("cms.new.auto.label.direction", "Direction")}>
              <Select
                value={value.overlay.direction}
                onValueChange={(v) =>
                  setOverlay({ direction: v as GradientDirection })
                }
              >
                <SelectTrigger className="h-9 w-44 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIRECTION_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {cmsNew(o.labelKey, o.fallback)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Row>
            <Row label={cmsNew("cms.new.auto.label.opacite_0_1", "Opacité (0–1)")}>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={value.overlay.opacity}
                className={cn(
                  CMS_PALETTE_TEXT_INPUT_CLASS,
                  "h-9 w-24 text-center tabular-nums",
                )}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  setOverlay({
                    opacity: Number.isFinite(n)
                      ? Math.min(1, Math.max(0, n))
                      : 0.4,
                  });
                }}
              />
            </Row>
          </div>
        )}
      </div>
    </div>
  );
}
